import { useMemo, useState } from 'react'
import { useGetFilteredProductCategories } from 'src/api/product-category'
import {
  ConfigurationCard,
  DrawingFilters,
  ExistingGroupOption,
  SelectedPositionLabel,
} from 'src/generated-types/configure-positions'
import { IPositionDrawing } from 'src/generated-types/position-drawing'
import AssignDrawingFooter from './AssignDrawingFooter'
import ConfigurationCardList from './ConfigurationCardList'
import ConfigureDrawingFilters from './ConfigureDrawingFilters'
import { PanelRoot, PanelShell } from './ConfigurePositionsPanel.style'
import DrawingPickerGrid from './DrawingPickerGrid'
import SelectedPositionsHeader from './SelectedPositionsHeader'
import { EMPTY_DRAWING_FILTERS, filterDrawings } from './drawingFilters.action'

interface ConfigurePositionsPanelProps {
  cards: ConfigurationCard[]
  highlightedKey: string | null
  onHighlight: (key: string | null) => void
  drawings: IPositionDrawing[]
  drawingsLoading: boolean
  selectedLabels: SelectedPositionLabel[]
  pickedDrawingId: number | null
  grouping: boolean
  groupName: string
  saving: boolean
  renameOnly: boolean
  editing: boolean
  existingGroups: ExistingGroupOption[]
  lockedGroup?: ExistingGroupOption
  suggestedGroupName: string
  showBridleLegFilter: boolean
  onClear: () => void
  onUnselect: (id: number) => void
  onPick: (drawingId: number) => void
  onGrouping: (value: boolean, suggestedName: string) => void
  onGroupName: (value: string) => void
  onAssign: () => void
  onRemoveSelected: () => void
  onEditCard: (card: ConfigurationCard) => void
  onRemoveCard: (card: ConfigurationCard) => void
  // Shown above everything else while a draft revision is open.
  revisionSection?: React.ReactNode
}

const ConfigurePositionsPanel: React.FC<ConfigurePositionsPanelProps> = ({
  cards,
  highlightedKey,
  onHighlight,
  drawings,
  drawingsLoading,
  selectedLabels,
  pickedDrawingId,
  grouping,
  groupName,
  saving,
  renameOnly,
  editing,
  existingGroups,
  lockedGroup,
  suggestedGroupName,
  showBridleLegFilter,
  onClear,
  onUnselect,
  onPick,
  onGrouping,
  onGroupName,
  onAssign,
  onRemoveSelected,
  onEditCard,
  onRemoveCard,
  revisionSection,
}) => {
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<DrawingFilters>(EMPTY_DRAWING_FILTERS)
  const { data: productCategories } = useGetFilteredProductCategories()
  const configuredCount = selectedLabels.filter(item => item.color).length
  const pickedDrawing = drawings.find(drawing => drawing.id === pickedDrawingId)

  const filteredDrawings = useMemo(() => filterDrawings(drawings, { search, filters }), [drawings, search, filters])

  const content =
    selectedLabels.length === 0 ? (
      <ConfigurationCardList
        cards={cards}
        drawings={drawings}
        highlightedKey={highlightedKey}
        onHighlight={onHighlight}
        onEditCard={onEditCard}
        onRemoveCard={onRemoveCard}
      />
    ) : (
      <>
        <SelectedPositionsHeader
          selectedLabels={selectedLabels}
          configuredCount={configuredCount}
          onClear={onClear}
          onUnselect={onUnselect}
        />
        <ConfigureDrawingFilters
          search={search}
          onSearch={setSearch}
          filters={filters}
          onFilters={setFilters}
          categories={productCategories ?? []}
          showBridleLegFilter={showBridleLegFilter}
          resultCount={filteredDrawings.length}
        />
        <DrawingPickerGrid
          drawings={filteredDrawings}
          loading={drawingsLoading}
          pickedDrawingId={pickedDrawingId}
          onPick={onPick}
        />
        <AssignDrawingFooter
          pickedDrawing={pickedDrawing}
          grouping={grouping}
          groupName={groupName}
          suggestedGroupName={suggestedGroupName}
          existingGroups={existingGroups}
          lockedGroup={lockedGroup}
          saving={saving}
          renameOnly={renameOnly}
          canRemove={configuredCount > 0 && !editing}
          onGrouping={onGrouping}
          onGroupName={onGroupName}
          onAssign={onAssign}
          onRemoveSelected={onRemoveSelected}
        />
      </>
    )

  if (!revisionSection) return <PanelRoot>{content}</PanelRoot>

  // The revision section scrolls out of view; below it the panel keeps its usual full-height layout.
  return (
    <PanelShell>
      {revisionSection}
      <PanelRoot sx={{ height: '100%', border: 'none !important' }}>{content}</PanelRoot>
    </PanelShell>
  )
}

export default ConfigurePositionsPanel
